import type { ResourceArticleRecord } from '@bcc/shared';
import { apiFetch } from './api';
import type { ResourceArticle, ResourceCategory, ResourceDifficulty } from '../data/investorResourceArticles';

const VALID_CATEGORIES = new Set<string>([
  'financing',
  'legal',
  'market-research',
  'property-analysis',
  'property-management',
]);

const VALID_DIFFICULTY = new Set<string>(['beginner', 'intermediate', 'advanced']);

export function normalizeResourceCategory(c: string): ResourceCategory {
  return VALID_CATEGORIES.has(c) ? (c as ResourceCategory) : 'property-analysis';
}

export function normalizeResourceDifficulty(d: string): ResourceDifficulty {
  return VALID_DIFFICULTY.has(d) ? (d as ResourceDifficulty) : 'beginner';
}

export function resourceRecordToArticle(r: ResourceArticleRecord): ResourceArticle {
  const published = r.published_at.includes('T') ? r.published_at.slice(0, 10) : r.published_at;
  return {
    slug: r.slug,
    title: r.title,
    description: r.description,
    category: normalizeResourceCategory(r.category),
    difficulty: normalizeResourceDifficulty(r.difficulty),
    readMinutes: r.read_minutes,
    publishedAt: published,
    tags: r.tags ?? [],
    body: r.body_md || undefined,
  };
}

/** Returns `null` when the API is unreachable; empty array when there are no published articles. */
export async function fetchPublishedResourceArticles(): Promise<ResourceArticle[] | null> {
  try {
    const r = await apiFetch<{ articles: ResourceArticleRecord[] }>('/api/public/resource-articles');
    return (r.articles ?? []).map(resourceRecordToArticle);
  } catch {
    return null;
  }
}

export async function fetchPublishedResourceArticleBySlug(slug: string): Promise<ResourceArticle | null> {
  try {
    const r = await apiFetch<{ article: ResourceArticleRecord }>(
      `/api/public/resource-articles/${encodeURIComponent(slug)}`,
    );
    return r.article ? resourceRecordToArticle(r.article) : null;
  } catch {
    return null;
  }
}
